import { supabase } from '../lib/supabase';

export const userPaymentMethodService = {
  /**
   * Get all payment methods for a user
   */
  async getUserPaymentMethods(userId) {
    try {
      const { data, error } = await supabase?.from('user_payment_methods')?.select('*')?.eq('user_id', userId)?.order('is_default', { ascending: false })?.order('created_at', { ascending: false});

      if (error) throw error;

      // Convert snake_case to camelCase
      return (data || [])?.map(method => ({
        id: method?.id,
        userId: method?.user_id,
        paymentType: method?.payment_type,
        cardBrand: method?.card_brand,
        cardLastFour: method?.card_last_four,
        cardExpMonth: method?.card_exp_month,
        cardExpYear: method?.card_exp_year,
        cardHolderName: method?.card_holder_name,
        nickname: method?.nickname,
        paypalEmail: method?.paypal_email,
        upiId: method?.upi_id,
        accountHolderName: method?.account_holder_name,
        accountLastFour: method?.account_last_four,
        bankName: method?.bank_name,
        stripePaymentMethodId: method?.stripe_payment_method_id,
        stripeCustomerId: method?.stripe_customer_id,
        isDefault: method?.is_default,
        isVerified: method?.is_verified,
        isBusiness: method?.is_business || false,
        createdAt: method?.created_at,
        updatedAt: method?.updated_at
      }));

    } catch (err) {
      console.error('Error fetching payment methods:', err);
      throw err;
    }
  },

  /**
   * Get a specific payment method by ID
   */
  async getPaymentMethodById(methodId) {
    try {
      const { data, error } = await supabase?.from('user_payment_methods')?.select('*')?.eq('id', methodId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        paymentType: data?.payment_type,
        cardBrand: data?.card_brand,
        cardLastFour: data?.card_last_four,
        cardExpMonth: data?.card_exp_month,
        cardExpYear: data?.card_exp_year,
        cardHolderName: data?.card_holder_name,
        nickname: data?.nickname,
        paypalEmail: data?.paypal_email,
        upiId: data?.upi_id,
        accountHolderName: data?.account_holder_name,
        accountLastFour: data?.account_last_four,
        bankName: data?.bank_name,
        stripePaymentMethodId: data?.stripe_payment_method_id,
        stripeCustomerId: data?.stripe_customer_id,
        isDefault: data?.is_default,
        isVerified: data?.is_verified,
        isBusiness: data?.is_business || false,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

    } catch (err) {
      console.error('Error fetching payment method:', err);
      throw err;
    }
  },

  /**
   * Create a new payment method
   */
  async createPaymentMethod(userId, methodData) {
    try {
      // If this is set as default, first unset any existing defaults
      if (methodData?.isDefault) {
        await supabase?.from('user_payment_methods')
          ?.update({ is_default: false })
          ?.eq('user_id', userId);
      }

      const insertData = {
        user_id: userId,
        payment_type: methodData?.type,
        nickname: methodData?.nickname || null,
        is_default: methodData?.isDefault || false,
        is_verified: false,
        is_business: methodData?.isBusiness || false
      };

      // Add type-specific fields
      if (methodData?.type === 'credit_card' || methodData?.type === 'debit_card') {
        // In production, card details should be tokenized with Stripe
        let cardNumber = null;
        
        if (methodData?.cardNumber) {
            cardNumber = methodData.cardNumber.replace(/\s/g, '');
            insertData.card_last_four = cardNumber.slice(-4);
        } else if (methodData?.cardLastFour) {
            insertData.card_last_four = methodData.cardLastFour;
        }

        insertData.card_brand = methodData?.cardBrand;
        
        // Mock Encryption for Prototype/Demo (In production, use backend encryption/tokenization)
        if (cardNumber) insertData.encrypted_card_number = btoa(`ENC_${cardNumber}`);
        if (methodData?.cvv) insertData.encrypted_security_code = btoa(`ENC_${methodData.cvv}`);

        // Populate Stripe IDs (Passed from frontend integration)
        if (methodData?.stripePaymentMethodId) insertData.stripe_payment_method_id = methodData.stripePaymentMethodId;
        if (methodData?.stripeCustomerId) insertData.stripe_customer_id = methodData.stripeCustomerId;

        insertData.card_holder_name = methodData?.cardHolderName;
        insertData.card_exp_month = parseInt(methodData?.expiryMonth);
        insertData.card_exp_year = parseInt(methodData?.expiryYear);
        // Note: In production, CVV should never be stored
      } else if (methodData?.type === 'paypal') {
        insertData.paypal_email = methodData?.paypalEmail;
      } else if (methodData?.type === 'upi') {
        insertData.upi_id = methodData?.upiId;
      } else if (methodData?.type === 'bank_transfer') {
        insertData.account_holder_name = methodData?.accountHolderName;
        insertData.account_last_four = methodData?.accountNumber?.slice(-4);
        insertData.bank_name = methodData?.bankName;
      }

      const { data, error } = await supabase?.from('user_payment_methods')?.insert(insertData)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        paymentType: data?.payment_type,
        cardBrand: data?.card_brand,
        cardLastFour: data?.card_last_four,
        cardExpMonth: data?.card_exp_month,
        cardExpYear: data?.card_exp_year,
        cardHolderName: data?.card_holder_name,
        nickname: data?.nickname,
        paypalEmail: data?.paypal_email,
        upiId: data?.upi_id,
        accountHolderName: data?.account_holder_name,
        accountLastFour: data?.account_last_four,
        bankName: data?.bank_name,
        isDefault: data?.is_default,
        isVerified: data?.is_verified,
        createdAt: data?.created_at
      };

    } catch (err) {
      console.error('Error creating payment method:', err);
      throw err;
    }
  },

  /**
   * Update a payment method
   */
  async updatePaymentMethod(methodId, methodData) {
    try {
      const updateData = {
        is_default: methodData?.isDefault,
        updated_at: new Date()?.toISOString()
      };

      if (methodData?.nickname !== undefined) {
        updateData.nickname = methodData.nickname;
      }
      if (methodData?.isBusiness !== undefined) {
        updateData.is_business = methodData.isBusiness;
      }

      // Only allow updating certain fields
      if (methodData?.cardExpMonth) {
        updateData.card_exp_month = parseInt(methodData?.cardExpMonth);
      }
      if (methodData?.cardExpYear) {
        updateData.card_exp_year = parseInt(methodData?.cardExpYear);
      }

      const { data, error } = await supabase?.from('user_payment_methods')?.update(updateData)?.eq('id', methodId)?.select()?.single();

      if (error) throw error;

      return data;

    } catch (err) {
      console.error('Error updating payment method:', err);
      throw err;
    }
  },

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(methodId) {
    try {
      const { error } = await supabase?.from('user_payment_methods')?.delete()?.eq('id', methodId);

      if (error) throw error;

      return true;

    } catch (err) {
      console.error('Error deleting payment method:', err);
      throw err;
    }
  },

  /**
   * Set a payment method as default
   */
  async setDefaultPaymentMethod(userId, methodId) {
    try {
      // First, unset all defaults
      await supabase?.from('user_payment_methods')?.update({ is_default: false })?.eq('user_id', userId);

      // Then set the new default
      const { data, error } = await supabase?.from('user_payment_methods')?.update({ is_default: true })?.eq('id', methodId)?.select()?.single();

      if (error) throw error;

      return data;

    } catch (err) {
      console.error('Error setting default payment method:', err);
      throw err;
    }
  }
};